import { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Play, BarChart, Zap, Settings, TrendingUp, Users, Phone } from "lucide-react";

interface ProductDemoProps {
  productType: "voice" | "analytics" | "outbound" | "integration";
}

const ProductDemo = ({ productType }: ProductDemoProps) => {
  const [activeMetric, setActiveMetric] = useState<string>("calls");

  const demoContent = {
    voice: {
      title: "Voice AI Platform Demo",
      tabs: [
        { 
          id: "conversation", 
          label: "Live Conversation", 
          icon: Phone,
          content: (
            <div className="space-y-6">
              <div className="aspect-video rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary/30 overflow-hidden">
                <div className="relative w-full h-full">
                  <video 
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    poster="/assets/demos/demo-poster.svg"
                  >
                    <source src="/assets/demos/voice-ai-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="text-center">
                      <Button variant="outline" className="mb-4 hover:bg-primary hover:text-white">
                        <Play className="w-6 h-6 mr-2" />
                        Watch Demo
                      </Button>
                      <p className="text-lg font-semibold text-white">Real Customer Interaction</p>
                      <p className="text-sm text-white/80">See our AI in action handling complex queries</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg glass-effect text-center">
                  <div className="text-2xl font-bold text-green-400">99.2%</div>
                  <div className="text-xs text-muted-foreground">Intent Recognition</div>
                </div>
                <div className="p-4 rounded-lg glass-effect text-center">
                  <div className="text-2xl font-bold text-blue-400">32ms</div>
                  <div className="text-xs text-muted-foreground">Avg Response Time</div>
                </div>
                <div className="p-4 rounded-lg glass-effect text-center">
                  <div className="text-2xl font-bold text-purple-400">4.9/5</div>
                  <div className="text-xs text-muted-foreground">CSAT Score</div>
                </div>
              </div>
            </div>
          )
        },
        { 
          id: "features", 
          label: "Key Features", 
          icon: Settings,
          content: (
            <div className="space-y-4">
              {[
                { title: "Multi-language Support", desc: "10+ languages and accents", progress: 95 },
                { title: "Sentiment Analysis", desc: "Real-time emotion detection", progress: 88 },
                { title: "Context Awareness", desc: "Multi-turn conversation memory", progress: 92 },
                { title: "Human Handoff", desc: "Seamless escalation with context", progress: 97 }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-4 rounded-lg glass-effect"
                >
                  <div className="flex justify-between mb-2">
                    <span className="font-semibold">{feature.title}</span>
                    <span className="text-sm text-primary">{feature.progress}%</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{feature.desc}</p>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${feature.progress}%` }}
                      transition={{ duration: 1, delay: idx * 0.1 }}
                      className="h-full bg-gradient-to-r from-primary to-accent"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          )
        }
      ]
    },
    analytics: {
      title: "Analytics Suite Demo",
      tabs: [
        { 
          id: "dashboard", 
          label: "Live Dashboard", 
          icon: BarChart,
          content: (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: "Active Calls", value: "127", trend: "+12%" },
                  { label: "Avg Duration", value: "4:32", trend: "-8%" },
                  { label: "Satisfaction", value: "4.8", trend: "+15%" },
                  { label: "Resolution", value: "92%", trend: "+5%" }
                ].map((metric, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-4 rounded-lg glass-effect cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => setActiveMetric(metric.label)}
                  >
                    <div className="text-2xl font-bold mb-1">{metric.value}</div>
                    <div className="text-xs text-muted-foreground mb-2">{metric.label}</div>
                    <div className="text-xs text-green-400 font-semibold">{metric.trend}</div>
                  </motion.div>
                ))}
              </div>
              <div className="aspect-[2/1] rounded-xl glass-effect p-6 flex items-center justify-center">
                <div className="text-center">
                  <TrendingUp className="w-12 h-12 text-primary mx-auto mb-3" />
                  <p className="font-semibold">Real-time Metrics Visualization</p>
                  <p className="text-sm text-muted-foreground">Live data streaming from all channels</p>
                </div>
              </div>
            </div>
          )
        },
        { 
          id: "insights", 
          label: "AI Insights", 
          icon: Zap,
          content: (
            <div className="space-y-4">
              {[
                { insight: "Peak call volume detected at 2:00 PM - 4:00 PM daily", impact: "Consider adding 2 more agents during this window" },
                { insight: "Billing inquiries increased 23% this week", impact: "Proactive email campaign recommended" },
                { insight: "Agent Sarah has 96% satisfaction rate", impact: "Promote best practices across team" },
                { insight: "Average wait time reduced by 45 seconds", impact: "Continue current optimization strategy" }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-5 rounded-lg glass-effect border-l-4 border-primary"
                >
                  <p className="font-semibold mb-2">{item.insight}</p>
                  <p className="text-sm text-muted-foreground italic">💡 {item.impact}</p>
                </motion.div>
              ))}
            </div>
          )
        }
      ]
    },
    outbound: {
      title: "Outbound Automation Demo",
      tabs: [
        { 
          id: "campaign", 
          label: "Campaign Flow", 
          icon: Zap,
          content: (
            <div className="space-y-6">
              <div className="relative">
                {[
                  { step: "1", title: "Lead Import", status: "Complete", count: "5,000" },
                  { step: "2", title: "AI Qualification", status: "In Progress", count: "3,847" },
                  { step: "3", title: "Personalized Outreach", status: "Scheduled", count: "2,156" },
                  { step: "4", title: "Follow-up Sequence", status: "Pending", count: "892" }
                ].map((step, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.15 }}
                    className="relative mb-6 last:mb-0"
                  >
                    <div className="flex items-center gap-4 p-5 rounded-xl glass-effect border border-primary/30">
                      <div className="w-12 h-12 rounded-full gradient-brand flex items-center justify-center text-white font-bold text-xl shrink-0">
                        {step.step}
                      </div>
                      <div className="flex-grow">
                        <div className="font-bold text-lg">{step.title}</div>
                        <div className="text-sm text-muted-foreground">{step.status} • {step.count} leads</div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                        step.status === "Complete" ? "bg-green-500/20 text-green-400" :
                        step.status === "In Progress" ? "bg-blue-500/20 text-blue-400" :
                        "bg-gray-500/20 text-gray-400"
                      }`}>
                        {step.status}
                      </div>
                    </div>
                    {idx < 3 && (
                      <div className="absolute left-6 top-[72px] w-0.5 h-6 bg-gradient-to-b from-primary to-accent" />
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )
        },
        { 
          id: "results", 
          label: "Campaign Results", 
          icon: TrendingUp,
          content: (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { metric: "Contact Rate", value: "73%", comparison: "+18% vs manual" },
                  { metric: "Qualified Leads", value: "1,247", comparison: "3.2x industry avg" },
                  { metric: "Cost per Lead", value: "$4.20", comparison: "-62% reduction" },
                  { metric: "Appointments Set", value: "418", comparison: "+156% increase" }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 rounded-xl glass-effect text-center"
                  >
                    <div className="text-sm text-muted-foreground mb-2">{item.metric}</div>
                    <div className="text-3xl font-bold mb-2 gradient-brand-text">{item.value}</div>
                    <div className="text-xs text-green-400 font-semibold">{item.comparison}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          )
        }
      ]
    },
    integration: {
      title: "Integration Hub Demo",
      tabs: [
        { 
          id: "connectors", 
          label: "Connected Systems", 
          icon: Settings,
          content: (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { name: "Salesforce", status: "Connected", sync: "2 min ago" },
                  { name: "HubSpot", status: "Connected", sync: "5 min ago" },
                  { name: "Zendesk", status: "Connected", sync: "1 min ago" },
                  { name: "Microsoft Dynamics", status: "Connected", sync: "3 min ago" },
                  { name: "SAP", status: "Connected", sync: "7 min ago" },
                  { name: "Custom API", status: "Connected", sync: "Live" }
                ].map((system, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-5 rounded-xl glass-effect text-center border border-primary/20"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 mx-auto mb-3 flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div className="font-bold mb-1">{system.name}</div>
                    <div className="text-xs text-green-400 mb-1">● {system.status}</div>
                    <div className="text-xs text-muted-foreground">Last sync: {system.sync}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          )
        }
      ]
    }
  };

  const demo = demoContent[productType];

  return (
    <Card className="p-8 md:p-12 glass-effect border-primary/30 bg-gradient-to-br from-background/50 to-background">
      <div className="flex items-center gap-3 mb-8">
        <Play className="w-10 h-10 text-primary" />
        <h3 className="text-3xl font-bold">{demo.title}</h3>
      </div>

      <Tabs defaultValue={demo.tabs[0].id} className="w-full">
        <TabsList className="grid w-full mb-8" style={{ gridTemplateColumns: `repeat(${demo.tabs.length}, 1fr)` }}>
          {demo.tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {demo.tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>

      <div className="mt-8 pt-8 border-t border-border">
        <div className="flex gap-4">
          <Button className="flex-1 gradient-brand text-white hover:opacity-90">
            Request Live Demo
          </Button>
          <Button variant="outline" className="flex-1 border-primary text-primary hover:bg-primary hover:text-white">
            Try Interactive Sandbox
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ProductDemo;
